import { useBrowserStore, ActivityEntry, ActivityType, ActivityStatus } from '@/stores/browserStore';
import {
  Monitor,
  MonitorOff,
  Camera,
  MousePointer,
  AlertCircle,
  Info,
  Wifi,
  Trash2,
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useRef, useEffect, useState } from 'react';

const TYPE_ICONS: Record<ActivityType, typeof Monitor> = {
  tab_attached: Monitor,
  tab_detached: MonitorOff,
  snapshot: Camera,
  action: MousePointer,
  error: AlertCircle,
  info: Info,
  relay: Wifi,
};

const STATUS_CONFIG: Record<ActivityStatus, { icon: typeof CheckCircle; color: string }> = {
  info: { icon: Info, color: 'text-blue-400' },
  success: { icon: CheckCircle, color: 'text-green-400' },
  warning: { icon: AlertTriangle, color: 'text-yellow-400' },
  error: { icon: XCircle, color: 'text-red-400' },
  progress: { icon: Loader2, color: 'text-blue-400' },
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function ActivityItem({ entry }: { entry: ActivityEntry }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const TypeIcon = TYPE_ICONS[entry.type];
  const statusConfig = STATUS_CONFIG[entry.status];
  const StatusIcon = statusConfig.icon;
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <div className="py-2 px-1 hover:bg-secondary/30 rounded transition-colors">
      <div
        className={`flex items-start gap-2 ${hasDetails ? 'cursor-pointer' : ''}`}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <div className={`mt-0.5 ${statusConfig.color}`}>
          {entry.status === 'progress' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <StatusIcon className="h-3.5 w-3.5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground">{entry.message}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <TypeIcon className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{formatTime(entry.timestamp)}</span>
            {hasDetails && (
              isExpanded ? (
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              )
            )}
          </div>
        </div>
      </div>
      {isExpanded && hasDetails && (
        <div className="mt-2 ml-6 p-2 bg-secondary/50 rounded text-xs font-mono overflow-x-auto">
          <pre className="text-muted-foreground whitespace-pre-wrap break-all">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function ActivityLog() {
  const { activities, clearActivities } = useBrowserStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to top when new activities arrive (since newest is at top)
  useEffect(() => {
    if (scrollRef.current && activities.length > 0) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activities.length]);

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col h-64">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="text-sm font-medium text-foreground">Activity Log</h2>
        {activities.length > 0 && (
          <button
            onClick={clearActivities}
            className="p-1.5 rounded hover:bg-secondary transition-colors"
            title="Clear log"
          >
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Activity List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3">
        {activities.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">No activity yet</p>
          </div>
        ) : (
          <div className="py-1 space-y-0.5">
            {activities.map((entry) => (
              <ActivityItem key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
